import {
  isDocumentSourceReference,
  type OperationFields,
  type SuggestionCitation,
  sourceProvenanceOf,
} from '@seen/core/models/compositionNote'

/* How much of a rejected source is echoed back to the model. Long enough to
 * identify what it sent, short enough that a pathological value cannot flood
 * the drop reason. */
const REJECTED_SOURCE_CHARS = 120

/* The citation an op carries, in the nested shape everything downstream reads,
 * narrowed structurally rather than off the op's declared type. Ops reach here
 * from places that do not agree on shape — the live tool call, a record
 * persisted through core's tolerant open union, and a record staged before
 * citations existed at all — so this is the one place the narrowing happens.
 * Only fields actually present come back, so spreading the result onto a
 * resolved spec never plants an `undefined` key on it.
 *
 * With evidence handles on, the citation arrives already nested: the server
 * resolved it from the passage the model cited and the model authored neither
 * half. The flat `source`/`evidence` arm below is what the tool schemas hand
 * the model with the flag OFF, and what records staged before the flip carry,
 * so it is still read and folded into the nested shape. Its rule is the one core
 * applies when it normalizes an old record: a quote whose source is missing or
 * carries no reference is dropped with it, because a quote on its own was
 * never a citation.
 *
 * `sourceProvenance` rides beside the citation, read through core's
 * `sourceProvenanceOf` — the whole list, or the legacy single object read as a
 * one-entry list — and surfaced independently of which citation arm matched
 * below, including the arm that finds no usable citation at all. A record can
 * carry valid provenance and an unusable citation together, and the two are
 * dropped on separate grounds. */
export function citationOf(op: OperationFields): SuggestionCitation {
  const provenance = sourceProvenanceOf(op)
  const provenanceField = provenance ? { sourceProvenance: provenance } : {}

  const nested = 'citation' in op ? op.citation : undefined
  if (
    typeof nested === 'object' &&
    nested !== null &&
    'reference' in nested &&
    typeof nested.reference === 'string'
  ) {
    const quote = 'quote' in nested ? nested.quote : undefined
    return {
      citation: {
        reference: nested.reference,
        ...(typeof quote === 'string' && quote.trim().length > 0 && { quote }),
      },
      ...provenanceField,
    }
  }
  const source = 'source' in op ? op.source : undefined
  if (
    typeof source !== 'object' ||
    source === null ||
    !('reference' in source) ||
    typeof source.reference !== 'string'
  ) {
    return provenanceField
  }
  const evidence = 'evidence' in op ? op.evidence : undefined
  return {
    citation: {
      reference: source.reference,
      ...(typeof evidence === 'string' &&
        evidence.trim().length > 0 && { quote: evidence }),
    },
    ...provenanceField,
  }
}

/* Reject a citation the clinician could not act on, before the op resolves.
 * A chip whose reference names an uncitable type opens nothing, so the excerpt
 * would assert a source the clinician cannot check — worse than no excerpt. The
 * whole edit is dropped rather than the citation silently stripped, so the
 * reason travels back to the model and it re-sends with a real source.
 * Validated against the RAW field, not `citationOf`'s normalization, so a
 * malformed source is an error instead of quietly disappearing.
 *
 * The reference is the only thing checked. A missing or nonsense label used to
 * fail here because the chip showed it; the chip is now named from the resolved
 * resource, so a label is neither needed nor trusted. */
export function citationIssue(
  opType: string,
  op: OperationFields
): { error: string } | null {
  const source = 'source' in op ? op.source : undefined
  if (source === undefined || source === null) {
    return null
  }
  const reference =
    typeof source === 'object' && 'reference' in source
      ? source.reference
      : undefined
  if (isDocumentSourceReference(reference)) {
    return null
  }
  return {
    error: `${opType}: source must reference a Composition, QuestionnaireResponse or DocumentReference as "ResourceType/<id>"; got ${JSON.stringify(source).slice(0, REJECTED_SOURCE_CHARS)}.`,
  }
}
